import { useState } from 'react'

import { useJourneyDevTools } from '@cash-web/shared-devtools-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

type Route = { trackingName: string; path: string; children?: Route[] }
const parseRoutes = (journeyRoutes: Route[]) => {
  const routes = []

  // Recursively grab all routes
  const paths = [{ prefix: '', routes: journeyRoutes }]
  while (paths.length > 0) {
    const current = paths.pop()
    if (!current) continue
    for (const route of current.routes) {
      if (route.children) {
        const path = route.path.replaceAll('*', '')
        const prefix = path[0] === '/' ? path : `/${path}`
        paths.push({ prefix, routes: route.children })
      } else {
        if (route.trackingName) {
          const newRoute = { name: route.trackingName, path: `${current.prefix}${route.path}` }
          routes.push(newRoute)
        }
      }
    }
  }

  // Filter out routes with parameters (naively looking for ":")
  const filteredRoutes = routes.filter(route => !route.path.includes(':'))

  return filteredRoutes
}

type RouteMenuItem = {
  id?: string
  name: string
  path: string
  timeStamp?: string
}

type UseCommandKSearchArgs = { setIsOpen: (isOpen: boolean) => void }
export const useCommandKSearch = ({ setIsOpen }: UseCommandKSearchArgs) => {
  const [value, setValue] = useState('')
  const [selectedMenuItem, setSelectedMenuItem] = useState<RouteMenuItem | null>(null)
  const journey = useJourneyContext()
  const { routes = [] } = useJourneyDevTools() ?? {}
  const parsedRoutes = parseRoutes(routes as unknown as Route[])
  const filteredRoutes: RouteMenuItem[] = parsedRoutes.filter(route =>
    route.name.toLowerCase().includes(value.toLowerCase())
  )
  const history: RouteMenuItem[] = JSON.parse(localStorage.getItem('journeys') ?? '[]').map(
    (journey: { journeyId: string; path: string; timeStamp: string }) => ({
      id: journey.journeyId,
      name: journey.path,
      path: journey.path,
      timeStamp: journey.timeStamp,
    })
  )
  history.sort((a, b) => new Date(b.timeStamp || 0).getTime() - new Date(a.timeStamp || 0).getTime())
  const menuItems = [...history.slice(0, 3), ...filteredRoutes, ...history.slice(3)]

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setValue(value)
    const newMenuItems = menuItems.filter(route => route.name.toLowerCase().includes(value.toLowerCase()))
    if (filteredRoutes.length > 0) {
      setSelectedMenuItem(newMenuItems[0])
    } else {
      setSelectedMenuItem(null)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedMenuItem) {
      navigate(selectedMenuItem.path)
    } else if (e.key === 'ArrowDown') {
      const nextIndex =
        menuItems.findIndex(
          ({ name, path, id }) =>
            name === selectedMenuItem?.name && path === selectedMenuItem?.path && id === selectedMenuItem?.id
        ) + 1
      if (nextIndex < menuItems.length) setSelectedMenuItem(menuItems[nextIndex])
    } else if (e.key === 'ArrowUp') {
      const prevIndex =
        menuItems.findIndex(
          ({ name, path, id }) =>
            name === selectedMenuItem?.name && path === selectedMenuItem?.path && id === selectedMenuItem?.id
        ) - 1
      if (prevIndex >= 0) setSelectedMenuItem(menuItems[prevIndex])
    }
  }

  const navigate = (path: string) => {
    setValue('')
    setIsOpen(false)
    journey.navigate(path)
  }

  return { value, onChange, onKeyDown, menuItems, selectedMenuItem, setSelectedMenuItem, navigate }
}
